import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ArrowLeft, FileText } from 'lucide-react';
import { describeActivity, PLATFORM_TEXT } from '../../../types/activity';
import type { ActivityEntry } from '../../../types/activity';
import UserActivityChart from './UserActivityChart';
import { BuildingActivityTable } from './UserActivityTables';
import { actionCount, actorLog, buildingRows, hasChangeLog, UserRow, weeklySeries } from './userActivityView';

const PAGE_SIZE = 200;

interface Props {
    row: UserRow;
    entries: ActivityEntry[];
    start: string;
    end: string;
    buildingName: (key: string) => string;
    onBack: () => void;
}

const UserActivityDetail: React.FC<Props> = ({ row, entries, start, end, buildingName, onBack }) => {
    const [visible, setVisible] = useState(PAGE_SIZE);
    const log = useMemo(() => actorLog(entries, row.id), [entries, row.id]);
    const series = useMemo(() => weeklySeries(log, start, end), [log, start, end]);
    const buildings = useMemo(() => buildingRows(log), [log]);
    const actions = log.reduce((sum, e) => sum + actionCount(e), 0);

    let lastDate = '';

    return (
        <div className="user-activity-detail">
            <Button variant="link" className="user-activity-back" onClick={onBack}>
                <ArrowLeft size={16} /> Alla användare
            </Button>
            <h2 className="user-activity-detail-title">{row.name}</h2>
            <p className="user-activity-muted">
                {row.lastActive ? `Senast aktiv ${row.lastActive.slice(0, 16)}` : 'Inte aktiv under perioden'}
                {' · '}{row.activeDays} aktiva dagar · {actions} åtgärder
            </p>

            <div className="user-activity-card">
                <UserActivityChart data={series} showActiveUsers={false} height={220} />
            </div>

            {buildings.length > 0 && (
                <div className="user-activity-card">
                    <h3 className="user-activity-section-title">Byggnader</h3>
                    <BuildingActivityTable rows={buildings} buildingName={buildingName} />
                </div>
            )}

            <div className="user-activity-card">
                <h3 className="user-activity-section-title">Logg</h3>
                {log.length === 0 && <p className="user-activity-muted">Ingen aktivitet under perioden.</p>}
                <ul className="user-activity-log">
                    {log.slice(0, visible).map((entry) => {
                        const showDate = entry.date !== lastDate;
                        lastDate = entry.date;
                        return (
                            <React.Fragment key={`${entry.date}/${entry.key}`}>
                                {showDate && <li className="user-activity-log-date">{entry.date}</li>}
                                <li className="user-activity-log-entry">
                                    <span className="ua-log-time">{entry.when.slice(11, 16)}</span>
                                    <span className="ua-log-text">
                                        {describeActivity(entry)}
                                        {entry.buildingKey && <span className="user-activity-muted"> · {buildingName(entry.buildingKey)}</span>}
                                    </span>
                                    <span className="ua-log-platform">{PLATFORM_TEXT[entry.platform]}</span>
                                    {hasChangeLog(entry) && (
                                        <Link className="ua-log-link" to={`/archive?date=${entry.date}`} title="Visa i Ändringslogg">
                                            <FileText size={14} /> Ändringslogg
                                        </Link>
                                    )}
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
                {log.length > visible && (
                    <Button variant="outline-secondary" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                        Visa fler ({log.length - visible} kvar)
                    </Button>
                )}
            </div>
        </div>
    );
};

export default UserActivityDetail;
